import React, { forwardRef } from 'react';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-loading-skeleton/dist/skeleton.css';
import styles from './style2.module.scss';
import { checkSameWeek, getSunday, getSaturday, getStartOfMonth, getEndOfMonth } from '../../../utils/date';

const DuetCalendarContainer = ({
  containerClassName,
  className,
  selectsRange,
  onClickReset,
  children,
  withPortal,
  highlightToday = true,
  isMonthPicker,
}) => {
  return (
    <div
      className={classNames(styles['calendar-container-wrapper'], containerClassName, {
        [styles['month-picker']]: isMonthPicker,
      })}
    >
      <CalendarContainer
        className={classNames(className, styles['date-picker-container'], {
          [styles['not-range']]: !selectsRange,
          [styles['highlight-today']]: highlightToday,
        })}
      >
        <div style={{ position: 'relative' }}>{children}</div>
      </CalendarContainer>
      {selectsRange && (
        <div className={styles['calendar-container-buttons-wrapper']}>
          <Button
            variant="outline-primary"
            className={classNames(styles.button, 'w-76', { [styles['reset-button-portal']]: withPortal })}
            onClick={onClickReset}
          >
            Reset
          </Button>
        </div>
      )}
    </div>
  );
};

const DuetCalendarHeader = ({
  monthDate,
  customHeaderCount,
  decreaseMonth,
  increaseMonth,
  monthsShown,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  decreaseYear,
  increaseYear,
  isMonthPicker,
  headerYearArrows,
  prevYearButtonDisabled,
  nextYearButtonDisabled,
}) => {
  const hidePrevMonthButton = (customHeaderCount === 1 && monthsShown !== 1) || prevMonthButtonDisabled;
  const hideNextMonthButton = (customHeaderCount === 0 && monthsShown !== 1) || nextMonthButtonDisabled;

  const ariaLabelPrevious = isMonthPicker ? 'Previous Year' : 'Previous Month';
  const prevMonthStyle = hidePrevMonthButton ? { visibility: 'hidden' } : null;
  const prevStyle = isMonthPicker ? null : prevMonthStyle;
  const prevOnClick = isMonthPicker ? decreaseYear : decreaseMonth;

  const ariaLabelNext = isMonthPicker ? 'Next Year' : 'Next Month';
  const nextMonthStyle = hideNextMonthButton ? { visibility: 'hidden' } : null;
  const nextStyle = isMonthPicker ? null : nextMonthStyle;
  const nextOnClick = isMonthPicker ? increaseYear : increaseMonth;

  const showHeaderYearArrows = headerYearArrows && !isMonthPicker;

  return (
    <div style={{ margin: '8px 0' }}>
      {showHeaderYearArrows && !prevYearButtonDisabled && (
        <button
          type="button"
          aria-label="Previous Year"
          className={classNames(styles['double-arrow-button'], styles['prev-double-arrow-button'])}
          onClick={decreaseYear}
        >
          <span className={styles['double-arrow']}>«</span>
        </button>
      )}
      <button
        type="button"
        aria-label={ariaLabelPrevious}
        className={classNames(
          'react-datepicker__navigation react-datepicker__navigation--previous',
          styles.navigation,
          { [styles['navigation-prev-with-double']]: showHeaderYearArrows }
        )}
        style={prevStyle}
        onClick={prevOnClick}
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{'<'}</span>
      </button>
      <span className={classNames('react-datepicker__current-month', styles['month-year'])}>
        {isMonthPicker
          ? monthDate.getFullYear()
          : monthDate.toLocaleString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
      </span>
      <button
        type="button"
        aria-label={ariaLabelNext}
        className={classNames('react-datepicker__navigation react-datepicker__navigation--next', styles.navigation, {
          [styles['navigation-next-with-double']]: showHeaderYearArrows,
        })}
        style={nextStyle}
        onClick={nextOnClick}
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{'>'}</span>
      </button>
      {showHeaderYearArrows && !nextYearButtonDisabled && (
        <button
          type="button"
          aria-label="Next Year"
          className={classNames(styles['double-arrow-button'], styles['next-double-arrow-button'])}
          onClick={increaseYear}
        >
          <span className={styles['double-arrow']}>»</span>
        </button>
      )}
    </div>
  );
};

const DatePickerButton = forwardRef(({ className, onClick }, ref) => (
  <button
    type="button"
    className={classNames('btn btn-light waves-effect float-right dropdown-toggle', className)}
    onClick={onClick}
    ref={ref}
  >
    Display
  </button>
));

const DatePickerInput = React.forwardRef(
  (
    {
      name,
      value,
      minDate,
      maxDate,
      dateFormat,
      placeholderText = 'mm/dd/yyyy',
      showCustomInput = true,
      customInputRender = ({ customInputClassNameProp }) => <DatePickerButton className={customInputClassNameProp} />,
      customInputClassName,
      popperModifiers,
      popperClassName,
      popperPlacement,
      onChange,
      onSelect,
      onBlur,
      selectsRange = false,
      startDate,
      endDate,
      monthsShown = 2,
      withPortal,
      portalId,
      disabled,
      isWeekPicker,
      weekPickerValue = 'sunday', // or saturday
      showMonthYearPicker,
      showFullMonthYearPicker,
      headerYearArrows,
      monthPickerValue = 'start', // or end
      className,
      wrapperClassName,
      containerClassName,
      onCalendarClose,
      onClickReset,
    },
    ref
  ) => {
    const handleChange = (changeValue, e) => {
      if (!onChange) {
        return;
      }

      if (isWeekPicker) {
        const weekValue = weekPickerValue === 'sunday' ? getSunday(changeValue) : getSaturday(changeValue);
        onChange(weekValue, e);
        return;
      }

      if (showMonthYearPicker) {
        const monthYearValue = monthPickerValue === 'start' ? getStartOfMonth(changeValue) : getEndOfMonth(changeValue);
        onChange(monthYearValue, e);
        return;
      }

      onChange(changeValue, e);
    };

    return (
      <DatePicker
        id={`date-picker-input-${name}`}
        dateFormat={dateFormat}
        selected={value}
        name={name}
        onChange={handleChange}
        onSelect={onSelect}
        onBlur={onBlur}
        placeholderText={placeholderText}
        className={className}
        wrapperClassName={wrapperClassName}
        autoComplete="off"
        popperModifiers={popperModifiers}
        popperPlacement={popperPlacement}
        popperClassName={popperClassName}
        minDate={minDate}
        maxDate={maxDate}
        selectsRange={selectsRange}
        startDate={startDate}
        portalId={portalId}
        endDate={endDate}
        monthsShown={monthsShown}
        withPortal={withPortal}
        fixedHeight
        disabledKeyboardNavigation
        focusSelectedMonth
        customInput={
          showCustomInput ? customInputRender({ customInputClassNameProp: customInputClassName }) : undefined
        }
        calendarContainer={(props) => (
          <DuetCalendarContainer
            {...props}
            selectsRange={selectsRange}
            onClickReset={onClickReset}
            withPortal={withPortal}
            containerClassName={containerClassName}
            isMonthPicker={showMonthYearPicker}
          />
        )}
        renderCustomHeader={(props) => (
          <DuetCalendarHeader
            {...props}
            monthsShown={monthsShown}
            isMonthPicker={showMonthYearPicker}
            headerYearArrows={headerYearArrows}
          />
        )}
        renderDayContents={(day, date) => {
          if (isWeekPicker && value) {
            const isSameWeek = checkSameWeek(date, value);

            return (
              <div
                className={styles.dayContainer}
                style={{
                  backgroundColor: isSameWeek && '#118bf0',
                  color: isSameWeek && '#ffffff',
                }}
              >
                {day}
              </div>
            );
          }

          return <div className={styles.dayContainer}>{day}</div>;
        }}
        monthClassName={() => {
          return styles.month;
        }}
        showMonthYearPicker={showMonthYearPicker}
        showFullMonthYearPicker={showFullMonthYearPicker}
        disabled={disabled}
        dayClassName={() => styles.day}
        onCalendarClose={onCalendarClose}
        ref={ref}
      />
    );
  }
);

export default DatePickerInput;
